import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, User, ArrowLeft } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import api from '../utils/api';
import { toast } from 'sonner';

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

const fetchPost = useCallback(async () => {
  try {
    const response = await api.get(`/blog/slug/${slug}`);
    setPost(response.data);
  } catch {
    toast.error("Failed to load blog post");
  } finally {
    setLoading(false);
  }
}, [slug]);

useEffect(() => {
  fetchPost();
}, [fetchPost]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <div className="flex items-center justify-center min-h-[60vh] bg-slate-50">
        <div className="flex flex-col items-center gap-4">
          
          {/* Spinner */}
          <div className="w-14 h-14 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />

          {/* Brand Text */}
          <div className="text-center">
            <h2 className="text-xl font-heading font-bold text-gradient">
              CodeSky
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              Loading blog post...
            </p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}


  if (!post) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <h1 className="text-3xl font-bold text-slate-900 mb-4">Post not found</h1>
          <Link to="/blog" className="text-primary hover:underline">Return to blog</Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <article className="pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-6">
          <Link to="/blog" className="inline-flex items-center space-x-2 text-primary hover:gap-3 transition-all mb-8" data-testid="back-to-blog">
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Blog</span>
          </Link>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            {post.featured_image && (
              <img
                src={post.featured_image}
                alt={post.title}
                className="w-full h-96 object-cover rounded-2xl mb-8"
              />
            )}

            <div className="flex items-center gap-6 text-slate-600 mb-6">
              <span className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                {formatDate(post.created_at)}
              </span>
              <span className="flex items-center gap-2">
                <User className="w-5 h-5" />
                {post.author}
              </span>
              <span className="bg-primary/10 text-primary px-3 py-1 rounded-full text-sm">
                {post.category}
              </span>
            </div>

            <h1 className="text-4xl lg:text-5xl font-heading font-bold text-slate-900 mb-6">
              {post.title}
            </h1>

            <div className="prose prose-lg max-w-none">
              <div className="text-slate-700 leading-relaxed whitespace-pre-wrap">
                {post.content}
              </div>
            </div>

            {post.tags && post.tags.length > 0 && (
              <div className="mt-12 pt-8 border-t border-slate-200">
                <h3 className="text-sm font-semibold text-slate-900 mb-3">Tags:</h3>
                <div className="flex flex-wrap gap-2">
                  {post.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="bg-slate-100 text-slate-700 px-4 py-2 rounded-full text-sm"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default BlogPost;
